import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { BookOpen, Calendar, Clock, User, Video, Users, PlayCircle, MessageCircle, Mail, Phone, Info, FileText, Edit, X, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import LessonStatusManager from '@/components/LessonStatusManager';
import ConnectionMethodSelector from '@/components/ConnectionMethodSelector';
import SendMessageModal from '@/components/SendMessageModal';
import { RescheduleRequestModal } from '@/components/RescheduleRequestModal';
import { pb } from '@/lib/pocketbase';
import { toast } from 'sonner';
import { useBookings } from '@/hooks/useBookings';

interface BookingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const BookingsModal = ({ isOpen, onClose }: BookingsModalProps) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showMethodSelector, setShowMethodSelector] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<any>(null);
  const [isMessageModalOpen, setIsMessageModalOpen] = useState(false);
  const [selectedTutorForMessage, setSelectedTutorForMessage] = useState<any>(null);
  const [selectedBookingForContact, setSelectedBookingForContact] = useState<any>(null);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [selectedBookingForNote, setSelectedBookingForNote] = useState<any>(null);
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [selectedBookingForReschedule, setSelectedBookingForReschedule] = useState<any>(null);
  const [isRescheduleModalOpen, setIsRescheduleModalOpen] = useState(false);

  // Use the fixed useBookings hook that handles both teaching and learning bookings for tutors
  const { data: bookings = [], isLoading, refetch } = useBookings();

  const handleStatusUpdate = () => {
    refetch();
  };

  const formatDate = (dateString: string) => {
    try {
      // Handle different date formats
      let date;
      
      if (dateString.includes('T') || dateString.includes(' ')) {
        // For ISO strings or datetime strings
        date = new Date(dateString);
      } else {
        // For date-only strings like "2025-06-25"
        const [year, month, day] = dateString.split('-').map(Number);
        date = new Date(year, month - 1, day);
      }

      // Check if date is valid
      if (isNaN(date.getTime())) {
        console.error('Invalid date:', dateString);
        return dateString; // Return original string if parsing fails
      }

      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', dateString, error);
      return dateString; // Return original string if formatting fails
    }
  };

  const formatTime = (timeString: string) => {
    try {
      const [hours, minutes] = timeString.split(':');
      const hour = parseInt(hours, 10);
      const minute = parseInt(minutes, 10);
      const date = new Date();
      date.setHours(hour, minute, 0, 0);
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      console.error('Error formatting time:', error);
      return timeString;
    }
  };

  const canJoinLesson = (booking: any) => {
    // Students can only join confirmed lessons
    // Tutors can join confirmed or pending lessons
    if (user?.role === 'tutor') {
      return booking.status === 'confirmed' || booking.status === 'pending';
    }
    return booking.status === 'confirmed';
  };

  const handleJoinLesson = (booking: any) => {
    setSelectedBooking(booking);
    setShowMethodSelector(true);
  };

  const handleMethodSelection = (method: 'webrtc' | 'jitsi' | 'google-meet') => {
    if (!selectedBooking) return;

    const role = user?.role === 'tutor' ? 'tutor' : 'student';
    
    if (method === 'webrtc') {
      // Use the default classroom - silent navigation without toast
      navigate(`/classroom?role=${role}&bookingId=${selectedBooking.id}`);
    } else if (method === 'jitsi') {
      // Open Tuternesta session
      const jitsiUrl = `https://meet.jit.si/tutornesta-${selectedBooking.id}`;
      window.open(jitsiUrl, '_blank');
      toast.success('Opened Tuternesta session in new tab');
    } else if (method === 'google-meet') {
      // Open Google Meet creation page
      const googleMeetUrl = 'https://meet.google.com/new';
      window.open(googleMeetUrl, '_blank');
      toast.info('Create a Google Meet room and share the link with the other participant');
    }

    // Reset state
    setShowMethodSelector(false);
    setSelectedBooking(null);
  };

  const handleCloseMethodSelector = () => {
    setShowMethodSelector(false);
    setSelectedBooking(null);
  };

  const handleMessageClick = async (booking: any) => {
    // Check if current user is the student in this booking (regardless of their role)
    if (booking.studentId === user?.id || booking.expand?.studentId?.id === user?.id) {
      // Current user is student, message the tutor
      // Use the expanded tutor data directly instead of doing another query
      if (booking.expand?.tutorId) {
        setSelectedTutorForMessage(booking.expand.tutorId);
        setIsMessageModalOpen(true);
      } else {
        toast.error('Could not find tutor information in booking');
        console.error('Missing tutor data in booking:', booking);
      }
    } else {
      // Current user is the tutor, message the student
      const studentId = booking.expand?.studentId?.id;
      const studentName = booking.expand?.studentId?.name || 'Student';
      
      console.log('Tutor messaging student:', { studentId, studentName, booking });
      
      if (studentId) {
        // Create a tutor-like object for the student to use with SendMessageModal
        const studentAsTutor = {
          id: studentId,
          user: studentId,
          name: studentName,
          expand: {
            user: {
              id: studentId,
              name: studentName
            }
          },
          subjects: [booking.subject] // Add the booking subject
        };
        
        setSelectedTutorForMessage(studentAsTutor);
        setIsMessageModalOpen(true);
      } else {
        toast.error('Could not find student information');
        console.error('Missing student data in booking:', booking);
      }
    }
  };

  const handleMessageModalClose = () => {
    setIsMessageModalOpen(false);
    setSelectedTutorForMessage(null);
  };

  const getOtherPartyName = (booking: any) => {
    // Check if current user is the student in this booking (regardless of their role)
    if (booking.studentId === user?.id || booking.expand?.studentId?.id === user?.id) {
      // Current user is student, show the tutor name
      return booking.expand?.tutorId?.expand?.user?.name || booking.expand?.tutorId?.name || 'Unknown Tutor';
    } else {
      // Current user is the tutor, show the student name
      return booking.expand?.studentId?.name || 'Unknown Student';
    }
  };

  const handleViewContact = (booking: any) => {
    setSelectedBookingForContact(booking);
    setIsContactModalOpen(true);
  };

  const handleContactModalClose = () => {
    setIsContactModalOpen(false);
    setSelectedBookingForContact(null);
  };

  const handleEditNote = (booking: any) => {
    setSelectedBookingForNote(booking);
    setNoteText(booking.notes || '');
    setIsNoteModalOpen(true);
  };

  const handleNoteModalClose = () => {
    setIsNoteModalOpen(false);
    setSelectedBookingForNote(null);
    setNoteText('');
  };

  const handleRescheduleClick = (booking: any) => {
    setSelectedBookingForReschedule(booking);
    setIsRescheduleModalOpen(true);
  };

  const handleSaveNote = async () => {
    if (!selectedBookingForNote) return;

    try {
      await pb.collection('bookings').update(selectedBookingForNote.id, {
        notes: noteText.trim()
      });

      toast('Note saved successfully!');
      refetch(); // Refresh the bookings list
      handleNoteModalClose();
    } catch (error: any) {
      console.error('Error saving note:', error);
      toast.error('Failed to save note');
    }
  };

  // Separate bookings by status
  const activeBookings = bookings.filter(b => b.status !== 'cancelled');
  const cancelledBookings = bookings.filter(b => b.status === 'cancelled');

  const renderBookingTable = (bookings: any[], title: string, emptyMessage: string, icon: React.ReactNode, borderColor: string) => {
    return (
      <Card className={`${borderColor} mb-4`}>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center text-lg">
            {icon}
            <span className="ml-2">{title} ({bookings.length})</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-3">
          {bookings.length === 0 ? (
            <div className="text-center py-6">
              <p className="text-gray-500 text-sm px-4">{emptyMessage}</p>
            </div>
          ) : (
            <>
              {/* Mobile Card Layout */}
              <div className="block lg:hidden">
                <div className="space-y-3 w-full">
                  {bookings.map((booking) => (
                    <Card key={booking.id} className="border border-gray-200 w-full">
                      <CardContent className="p-3">
                        <div className="space-y-3 w-full min-w-0">
                          <div className="flex items-start justify-between gap-2 w-full">
                            <div className="flex items-center space-x-2 min-w-0 flex-1">
                              <BookOpen className="h-4 w-4 text-blue-500 shrink-0" />
                              <span className="font-medium text-sm truncate">{booking.subject}</span>
                            </div>
                            <div className="shrink-0">
                              <LessonStatusManager 
                                item={booking} 
                                onStatusUpdate={handleStatusUpdate}
                              />
                            </div>
                          </div>
                          
                          <div className="flex items-center space-x-2 min-w-0">
                            <User className="h-4 w-4 text-gray-400 shrink-0" />
                            <span className="text-sm text-gray-600 truncate">{getOtherPartyName(booking)}</span>
                          </div>
                          
                          <div className="flex flex-col gap-1 text-xs text-gray-500">
                            <div className="flex items-center space-x-1">
                              <Calendar className="h-3 w-3 shrink-0" />
                              <span className="truncate">{formatDate(booking.date)}</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <Clock className="h-3 w-3 shrink-0" />
                              <span className="truncate">{formatTime(booking.startTime)} - {formatTime(booking.endTime)}</span>
                            </div>
                          </div>
                          
                          {booking.notes && (
                            <div className="flex items-start space-x-2 pt-1 min-w-0">
                              <FileText className="h-3 w-3 text-gray-400 shrink-0 mt-0.5" />
                              <span className="text-xs text-gray-600 italic truncate">{booking.notes}</span>
                            </div>
                          )}
                          
                          <div className="grid grid-cols-3 gap-1.5 pt-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleMessageClick(booking)}
                              className="text-xs h-8 min-w-0 overflow-hidden"
                            >
                              <MessageCircle className="h-3 w-3 mr-1 shrink-0" />
                              <span className="truncate">Message</span>
                            </Button>
                            
                            {/* Reschedule button - only for non-cancelled bookings */}
                            {booking.status !== 'cancelled' && (
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handleRescheduleClick(booking)}
                                className="text-xs h-8 min-w-0 overflow-hidden"
                              >
                                <RefreshCw className="h-3 w-3 mr-1 shrink-0" />
                                <span className="truncate">Reschedule</span>
                              </Button>
                            )}
                            
                            {/* Show contact only if current user is the tutor */}
                            {!(booking.studentId === user?.id || booking.expand?.studentId?.id === user?.id) && (booking.studentEmail || booking.studentPhone) ? (
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handleViewContact(booking)}
                                className="text-xs h-8 min-w-0 overflow-hidden"
                              >
                                <Info className="h-3 w-3 mr-1 shrink-0" />
                                <span className="truncate">Contact</span>
                              </Button>
                            ) : (
                              /* Show note editing if no contact info */
                              !(booking.studentId === user?.id || booking.expand?.studentId?.id === user?.id) && (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => handleEditNote(booking)}
                                  className="text-xs h-8 min-w-0 overflow-hidden"
                                >
                                  <Edit className="h-3 w-3 mr-1 shrink-0" />
                                  <span className="truncate">Note</span>
                                </Button>
                              )
                            )}
                            
                            <Button 
                              size="sm" 
                              onClick={() => handleJoinLesson(booking)}
                              disabled={!canJoinLesson(booking)}
                              className={`col-span-3 text-xs h-8 min-w-0 overflow-hidden ${
                                !canJoinLesson(booking) 
                                  ? 'opacity-50 cursor-not-allowed' 
                                  : ''
                              }`}
                              title={
                                !canJoinLesson(booking) 
                                  ? user?.role === 'student' 
                                    ? 'Available after tutor approval' 
                                    : 'Available for confirmed lessons'
                                  : 'Join lesson'
                              }
                            >
                              <Video className="h-3 w-3 mr-1 shrink-0" />
                              <span className="truncate">Join Lesson</span>
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              {/* Desktop Table Layout */}
              <div className="hidden lg:block w-full">
                <div className="w-full overflow-x-auto">
                  <Table className="w-full min-w-[600px]">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[20%] min-w-[120px]">Subject</TableHead>
                        <TableHead className="w-[20%] min-w-[140px]">Other Party</TableHead>
                        <TableHead className="w-[15%] min-w-[100px]">Date</TableHead>
                        <TableHead className="w-[20%] min-w-[120px]">Time</TableHead>
                        <TableHead className="w-[10%] min-w-[100px]">Status</TableHead>
                        <TableHead className="w-[15%] min-w-[150px]">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {bookings.map((booking) => (
                        <TableRow key={booking.id}>
                          <TableCell className="max-w-0">
                            <div className="flex items-center space-x-2 min-w-0">
                              <BookOpen className="h-4 w-4 text-blue-500 shrink-0" />
                              <span className="truncate">{booking.subject}</span>
                            </div>
                          </TableCell>
                          <TableCell className="max-w-0">
                            <div className="flex items-center space-x-2 min-w-0">
                              <User className="h-4 w-4 text-gray-400 shrink-0" />
                              <span className="truncate">{getOtherPartyName(booking)}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center space-x-2">
                              <Calendar className="h-4 w-4 text-gray-400 shrink-0" />
                              <span className="text-sm whitespace-nowrap">{formatDate(booking.date)}</span>
                            </div>
                          </TableCell>
                          <TableCell className="max-w-0">
                            <div className="flex items-center space-x-2 min-w-0">
                              <Clock className="h-4 w-4 text-gray-400 shrink-0" />
                              <span className="text-sm truncate">{formatTime(booking.startTime)} - {formatTime(booking.endTime)}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <LessonStatusManager 
                              item={booking} 
                              onStatusUpdate={handleStatusUpdate}
                            />
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1 min-w-0">
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handleMessageClick(booking)}
                                className="text-xs shrink-0"
                              >
                                <MessageCircle className="h-3 w-3" />
                                <span className="hidden xl:inline ml-1">Message</span>
                              </Button>
                              
                              {/* Reschedule button */}
                              {booking.status !== 'cancelled' && (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => handleRescheduleClick(booking)}
                                  className="text-xs shrink-0"
                                >
                                  <RefreshCw className="h-3 w-3" />
                                  <span className="hidden xl:inline ml-1">Reschedule</span>
                                </Button>
                              )}
                              
                              {!(booking.studentId === user?.id || booking.expand?.studentId?.id === user?.id) && (booking.studentEmail || booking.studentPhone) && (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => handleViewContact(booking)}
                                  className="text-xs shrink-0"
                                >
                                  <Info className="h-3 w-3" />
                                  <span className="hidden xl:inline ml-1">Contact</span>
                                </Button>
                              )}
                              {!(booking.studentId === user?.id || booking.expand?.studentId?.id === user?.id) && (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => handleEditNote(booking)}
                                  className="text-xs shrink-0"
                                >
                                  <Edit className="h-3 w-3" />
                                  <span className="hidden xl:inline ml-1">Note</span>
                                </Button>
                              )}
                              <Button 
                                size="sm" 
                                onClick={() => handleJoinLesson(booking)}
                                disabled={!canJoinLesson(booking)}
                                className={`text-xs shrink-0 ${
                                  !canJoinLesson(booking) 
                                    ? 'opacity-50 cursor-not-allowed' 
                                    : ''
                                }`}
                                title={
                                  !canJoinLesson(booking) 
                                    ? user?.role === 'student' 
                                      ? 'Available after tutor approval' 
                                      : 'Available for confirmed lessons'
                                    : 'Join lesson'
                                }
                              >
                                <Video className="h-3 w-3" />
                                <span className="hidden xl:inline ml-1">Join</span>
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-[95vw] w-full h-[90vh] max-h-[90vh] overflow-hidden p-0">
          <DialogHeader className="px-6 py-4 border-b">
            <div className="flex items-center justify-between">
              <DialogTitle className="text-xl font-bold">
                {user?.role === 'tutor' ? 'My Sessions' : 'My Lessons'}
              </DialogTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="h-6 w-6 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-gray-600 text-sm mt-1">
              {user?.role === 'tutor' 
                ? 'Manage your sessions with students'
                : 'View your booked lessons and join sessions'
              }
            </p>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {isLoading ? (
              <div className="flex items-center justify-center min-h-[200px]">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-4 text-sm">Loading bookings...</span>
              </div>
            ) : (
              <div className="space-y-4 max-w-full overflow-hidden">
                {/* Active Bookings */}
                {renderBookingTable(
                  activeBookings,
                  user?.role === 'tutor' ? 'Student Bookings' : 'My Booked Lessons',
                  user?.role === 'tutor' 
                    ? 'No student bookings yet. Students\' session requests will appear here.'
                    : 'No booked lessons yet. Lessons you book with tutors will appear here.',
                  <Users className="h-5 w-5 text-blue-600" />,
                  'border-l-4 border-l-blue-500'
                )}

                {/* Cancelled Bookings */}
                {renderBookingTable(
                  cancelledBookings,
                  'Cancelled Lessons',
                  'No cancelled lessons.',
                  <PlayCircle className="h-5 w-5 text-red-600" />,
                  'border-l-4 border-l-red-500'
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Connection Method Selector Modal */}
      <Dialog open={showMethodSelector} onOpenChange={handleCloseMethodSelector}>
        <DialogContent className="sm:max-w-lg w-[95vw] max-h-[90vh] overflow-y-auto mx-auto my-auto">
          <DialogHeader className="pb-2">
            <DialogTitle className="text-lg text-center">
              Start your lesson
            </DialogTitle>
          </DialogHeader>
          {selectedBooking && (
            <div className="space-y-3">
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-sm text-gray-600 text-center">
                  <strong>{selectedBooking.subject}</strong> session
                </p>
                <p className="text-xs text-gray-500 text-center mt-1">
                  {formatDate(selectedBooking.date)} at {formatTime(selectedBooking.startTime)}
                </p>
              </div>
              <div className="px-2">
                <ConnectionMethodSelector
                  onSelectMethod={handleMethodSelection}
                  bookingId={selectedBooking.id}
                  isVisible={true}
                  userRole={user?.role}
                />
              </div>
              {user?.role === 'student' && (
                <div className="p-2 bg-yellow-50 border border-yellow-200 rounded-lg mx-2">
                  <p className="text-xs text-yellow-800">
                    <strong>Note:</strong> If you have any problem, check your messages for updates from the tutor.
                  </p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <SendMessageModal
        isOpen={isMessageModalOpen}
        onClose={handleMessageModalClose}
        tutor={selectedTutorForMessage}
      />

      {/* Contact Information Modal */}
      <Dialog open={isContactModalOpen} onOpenChange={handleContactModalClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <User className="h-5 w-5 mr-2" />
              Student Contact Information
            </DialogTitle>
          </DialogHeader>
          {selectedBookingForContact && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-medium text-sm text-gray-700 mb-2">Session Details</h3>
                <div className="space-y-1 text-sm">
                  <div><strong>Subject:</strong> {selectedBookingForContact.subject}</div>
                  <div><strong>Date:</strong> {formatDate(selectedBookingForContact.date)}</div>
                  <div><strong>Time:</strong> {formatTime(selectedBookingForContact.startTime)} - {formatTime(selectedBookingForContact.endTime)}</div>
                  <div><strong>Student:</strong> {selectedBookingForContact.expand?.studentId?.name || 'Unknown Student'}</div>
                </div>
              </div>
              
              <div className="space-y-3">
                <h3 className="font-medium text-sm text-gray-700">Contact Information</h3>
                
                {selectedBookingForContact.studentEmail && (
                  <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <Mail className="h-4 w-4 text-blue-600" />
                      <span className="text-sm font-medium">Email</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-600">{selectedBookingForContact.studentEmail}</span>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          window.location.href = `mailto:${selectedBookingForContact.studentEmail}?subject=Tutoring Session - ${selectedBookingForContact.subject}&body=Hi! This is regarding our ${selectedBookingForContact.subject} session on ${formatDate(selectedBookingForContact.date)} at ${formatTime(selectedBookingForContact.startTime)}.`;
                        }}
                        className="h-6 px-2 text-xs"
                      >
                        Send Email
                      </Button>
                    </div>
                  </div>
                )}
                
                {selectedBookingForContact.studentPhone && (
                  <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <Phone className="h-4 w-4 text-green-600" />
                      <span className="text-sm font-medium">Phone</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-600">{selectedBookingForContact.studentPhone}</span>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          window.location.href = `tel:${selectedBookingForContact.studentPhone}`;
                        }}
                        className="h-6 px-2 text-xs"
                      >
                        Call
                      </Button>
                    </div>
                  </div>
                )}
                
                {!selectedBookingForContact.studentEmail && !selectedBookingForContact.studentPhone && (
                  <div className="text-center py-4">
                    <p className="text-gray-500 text-sm">No contact information available for this student.</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Note Editing Modal */}
      <Dialog open={isNoteModalOpen} onOpenChange={handleNoteModalClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <FileText className="h-5 w-5 mr-2" />
              Edit Session Note
            </DialogTitle>
          </DialogHeader>
          {selectedBookingForNote && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-medium text-sm text-gray-700 mb-2">Session Details</h3>
                <div className="space-y-1 text-sm">
                  <div><strong>Subject:</strong> {selectedBookingForNote.subject}</div>
                  <div><strong>Date:</strong> {formatDate(selectedBookingForNote.date)}</div>
                  <div><strong>Time:</strong> {formatTime(selectedBookingForNote.startTime)} - {formatTime(selectedBookingForNote.endTime)}</div>
                  <div><strong>Student:</strong> {selectedBookingForNote.expand?.studentId?.name || 'Unknown Student'}</div>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="session-note">Session Note</Label>
                <Textarea
                  id="session-note"
                  placeholder="Add notes about this session (progress, homework, next steps, etc.)"
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  rows={4}
                />
              </div>
              
              <div className="flex justify-end space-x-2">
                <Button
                  variant="outline"
                  onClick={handleNoteModalClose}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveNote}
                >
                  Save Note
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reschedule Request Modal */}
      {selectedBookingForReschedule && (
        <RescheduleRequestModal
          isOpen={isRescheduleModalOpen}
          onClose={() => {
            setIsRescheduleModalOpen(false);
            setSelectedBookingForReschedule(null);
            refetch(); // Refresh bookings after reschedule
          }}
          booking={selectedBookingForReschedule}
        />
      )}
    </>
  );
};

export default BookingsModal;